import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Flex, Form, Input } from 'antd';
import { changePassword } from '@/features/homepage/api/ApiChangePassword';
import storage from '@/utils/storage';
import { ApiGetUserNameInfo } from '@/features/homepage/api/ApiGetUserNameInfo';
import { ERR_MESSAGE, REGEX_PASSWORD_FORMAT, REGEX_PASSWORD_LENGTH, RESPONSE_ERR, RESPONSE_MESSAGE } from '@/constant';
import Sidebar from "react-sidebar";
import { SubSideMenu } from '@/components/SubSideMenu';
import { SubHeader } from '@/components/SubHeader';
import { LoadingSpinner } from '@/components/SpinnerOverlay';

// SCN09 - Form 1: Create/Change Password page
const mql = window.matchMedia(`(min-width: 800px)`);

const CreatePasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [Id, setId] = useState('');
  const [CustomerName, setCustomerName] = useState('');
  const [passwordNotMatchError, setPasswordNotMatchError] = useState('');
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const inputRef = useRef(null);
  const [formErrors] = Form.useForm();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navBarContent, setNavBarContent] = useState('');
  const [navLogoContent, setNavLogoContent] = useState('');
  const [sidebarDocked, setSidebarDocked] = useState(mql.matches);

  // Listen for changes in screen size to dock/undock sidebar
  mql.addListener(() => {
    setSidebarDocked(mql.matches);
  });

  // Fetch user info and handle redirect logic on mount
  useEffect(() => {
    const dataInput = {
      login_id: location.state?.id,
      password: location.state?.dealer_cd,
    };
    const type = storage.getDpsdType();
    const _id = storage.getId();

    setId(_id);

    // Redirect to login if no credentials are found
    const token = storage.getToken();
    if (!dataInput.login_id && !dataInput.password && !token) {
      navigate("/login");
    }

    // Fetch customer/user info from API
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await ApiGetUserNameInfo();
        if (res) {
          setCustomerName(res.name);
          setNavBarContent(res.name);
          setNavLogoContent(res.vkorg);
        }
        setLoading(false);
      } catch (error) {
        setError(ERR_MESSAGE.WAS001_MESSAGE);
      } finally {
        setLoading(false);
      }


      focusID();
    }
    fetchData();

  }, [location.state?.dealer_cd, location.state?.id, navigate]);

  // Handle password change form submission
  const handleSubmit = async (values: any) => {
    setLoading(true);
    const data = {
      password: values.currentPassword || null,
      new_password: values.newPassword || null,
    };
    try {
      const res = await changePassword(data);
      if (res) {
        setShowSuccessModal(true);
        setLoading(false);
        navigate("/create-password-inform", { state: { showSuccessModal: true } });
      }
    } catch (error) {
      // Handle specific error codes from API
      if (error.response.data.error_code == RESPONSE_ERR.E0501) {
        setPasswordNotMatchError(ERR_MESSAGE.E0501_MESSAGE);

      } else if (error.response.data.error_code == RESPONSE_ERR.E0502) {
        setPasswordNotMatchError(ERR_MESSAGE.E0501_MESSAGE);
      } else if (error.response.data.error_code == ERR_MESSAGE.WAS002_MESSAGE) {
        setPasswordNotMatchError(ERR_MESSAGE.WAS002_MESSAGE);
      } else {
        setPasswordNotMatchError(ERR_MESSAGE.WAS002_MESSAGE);
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // Focus input field when component mounts
  const focusID = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }

  // Show loading spinner while loading
  if (loading == true) {
    return <LoadingSpinner />;
  }

  // UI render
  return (
    <div style={{ display: loading ? 'none' : 'block' }}>
      <Sidebar
        sidebar={<SubSideMenu toggleMenu={() => setSidebarOpen(false)} companyCode={navLogoContent} nameUser={navBarContent} />}
        open={sidebarOpen}
        onSetOpen={setSidebarOpen}
        docked={sidebarDocked}
        transitions={false}
      >
        <SubHeader
          toggleMenu={() => setSidebarOpen(true)}
          nameUser={navBarContent}
          vkorg={navLogoContent}
        />
        <div className="container-fluid padding-content background-cover">
          <Fragment>
            <ol className="breadcrumb">
              <li className="breadcrumb-item active">
                {/* <span className='text text-primary'>ホーム</span> */}
              </li>
              <li className='text-dark'>パスワードの変更</li>
            </ol>
            <div className="jumbotron">
              <div className="section">
                <div className="card-body">
                  <Form
                    name="change-setting"
                    layout="vertical"
                    onFinish={handleSubmit}
                    size="large"
                    validateTrigger="onSubmit"
                    form={formErrors}
                  >
                    <ul className="list-group list-group-flush">
                      <li className="list-group-item" style={{ paddingBottom: '0px', paddingTop: '0.25rem' }}>
                        <p className='text-coca'>{error}</p>
                        <p className='text-coca'>{passwordNotMatchError}</p>
                        <p>ID:{Id}（{CustomerName}）</p>
                        <span>
                          パスワードを変更してください
                          <br />
                          現在の仮のパスワードを入力してください
                          <br />
                          0Mから始まる番号です
                        </span>
                        <Flex vertical gap={12}>
                          <Form.Item
                            name="currentPassword"
                            rules={[
                              { required: true, message: "変更前のパスワードが入力必須" },
                            ]}
                          >
                            <Input.Password className='input-primary' ref={inputRef} size="middle" />
                          </Form.Item>

                        </Flex>
                      </li>

                      <li className="list-group-item" style={{ paddingBottom: '0px', paddingTop: '0.25rem' }}>
                        <span>新しいパスワードを入力してください</span>
                        <br />
                        <span style={{ fontSize: '13.5px' }}>
                          ※半角英字大文字・小文字と数字、特殊記号 (！＃＄等)をそれぞれ1文字以上使用し、 10文字以上で設定してください。
                        </span>
                        <Flex vertical gap={12}>
                          <Form.Item
                            name="newPassword"
                            validateFirst={true}
                            rules={[
                              { required: true, message: "変更後のパスワードが入力必須" },
                              ({ getFieldValue }) => ({

                                validator(_, value) {
                                  if (!value || getFieldValue('currentPassword') !== value) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(new Error('変更後のパスワードは変更前のパスワードと違う値を入力してください'));
                                },
                              }),
                              {
                                pattern: REGEX_PASSWORD_LENGTH,
                                message: "パスワードは 大文字、小文字、数字、特殊記号を含め10文字以上、32文字以内である必要があります"
                              },
                              {
                                pattern: REGEX_PASSWORD_FORMAT,
                                message: "パスワードは 大文字、小文字、数字、特殊記号を含め10文字以上、32文字以内である必要があります"
                              },
                            ]}
                          >
                            <Input.Password className='input-primary' size="middle" />
                          </Form.Item>
                        </Flex>
                      </li>

                      <li className="list-group-item" style={{ paddingBottom: '0px', paddingTop: '0.25rem' }}>
                        <span>確認もう一度新しいパスワードを入力してください </span>
                        <Flex vertical gap={12}>
                          <Form.Item
                            name="reNewPassword"
                            validateFirst={true}
                            rules={[
                              { required: true, message: "変更後のパスワード（確認）が入力必須" },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(new Error('変更後のパスワードと一致していません'));
                                },
                              }),
                            ]}
                          >
                            <Input.Password className='input-primary' size="middle" />
                          </Form.Item>
                        </Flex>
                      </li>
                    </ul>
                    <ul className="list-group list-group-flush">
                      <li className="list-group-item" style={{ paddingTop: '0.25rem' }}>
                        <span>上記の登録でよろしければ「確定」を押してください</span>
                        <div className="d-flex justify-content-center">
                          <Button htmlType='submit' className="btn bg-coca col-md-3 col-sm-6" >
                            <span className="text-white" style={{ fontSize: '14px' }}  >確定 </span>
                          </Button>
                        </div>
                      </li>
                    </ul>
                  </Form>
                </div>
              </div>
            </div>
          </Fragment>
        </div>
      </Sidebar>
    </div>
  );
};

export default CreatePasswordPage;
